// steps/UserSigninRequestOtpStep.tsx

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import ErrorMessage from '@/components/common/ErrorMessage';


import { useApp } from '@/contexts/AppContext';

import { BasicResponse, LookupItem } from '@/core/models/common-types';
import { extractErrorMessage } from '@/core/utils/utils';

import { OtpChannel } from '@/core/enums/otp-channel.enum';

import { userSigninRequestOtpSchema, UserSigninRequestOtpFormValues } from '../schemas/userSigninRequestOtpSchema';

import { UserSigninAction, UserSigninState } from '../states/userSigninReducer';
import axios from 'axios';
import CustomCardRadioButton from '@/components/form/CustomCardRadioButton';

interface UserSigninRequestOtpStepProps {
  onNext: (data: UserSigninRequestOtpFormValues) => void;
  onPrevious: () => void;
  state: UserSigninState;
  dispatch: React.Dispatch<UserSigninAction>;
}

const UserSigninRequestOtpStep: React.FC<UserSigninRequestOtpStepProps> = ({ onNext, onPrevious, state, dispatch }) => {
  const { isLoading, baseUrl } = useApp();

  const [error, setError] = useState<BasicResponse | null>(null);

  const otpOptions: LookupItem<number>[] = [];

  if (state.phoneNumber?.trim()) {
    otpOptions.push({
      id: OtpChannel.Sms,
      code: 'SMS',
      name: `SMS (${state.phoneNumber})`,
    });
  }

  if (state.email?.trim()) {
    otpOptions.push({
      id: OtpChannel.Email,
      code: 'EMAIL',
      name: `Email (${state.email})`,
    });
  }

  const { control, handleSubmit } = useForm<UserSigninRequestOtpFormValues>({
    resolver: zodResolver(userSigninRequestOtpSchema),
    defaultValues: {
      otpChannel: state.otpChannel ?? (state.phoneNumber ? OtpChannel.Sms : OtpChannel.Email),
    },
  });

  const submitForm = async (data: UserSigninRequestOtpFormValues) => {
    try {
      setError(null);
      await axios.post(`${baseUrl}/api/v2/UserSignin/SendOtp`, {
        loginSessionId: state.loginSessionId,
        otpChannel: data.otpChannel,
      });

      dispatch({ type: 'UPDATE', payload: { otpChannel: data.otpChannel } });

      onNext(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <>
      <ErrorMessage error={error} />

      <form onSubmit={handleSubmit(submitForm)} noValidate>
        <div className="mb-3">
          <h5 className="fw-semibold">Choose Verification Method</h5>

          <p className="text-muted mb-0">Select where you would like to receive your one-time verification code.</p>
        </div>

        <CustomCardRadioButton<number, UserSigninRequestOtpFormValues>
          control={control}
          name="otpChannel"
          label="Verification Method"
          options={otpOptions}
          required
        />

        <div className="d-flex justify-content-between mt-4">
          <button type="button" className="btn btn-outline-secondary" onClick={onPrevious} disabled={isLoading}>
            Back
          </button>

          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Sending OTP...' : 'Send OTP'}
          </button>
        </div>
      </form>
    </>
  );
};

export default UserSigninRequestOtpStep;
